import React, { useState, useEffect } from 'react';
import { DollarSign, Clock, CheckCircle, XCircle, AlertCircle, Eye, Edit, Save } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../../../../services/api';

const BillingManagementTab = ({ ticket, onTicketUpdate }) => {
    const [billingHours, setBillingHours] = useState([]);
    const [loading, setLoading] = useState(true);
    const [billingEnabled, setBillingEnabled] = useState(false);
    const [showEnableForm, setShowEnableForm] = useState(false);
    const [billingConfig, setBillingConfig] = useState({
        hourly_rate: '',
        billing_notes: ''
    });
    const [processingAction, setProcessingAction] = useState(null);
    const [enablingBilling, setEnablingBilling] = useState(false);
    const [disablingBilling, setDisablingBilling] = useState(false);

    useEffect(() => {
        if (ticket) {
            setBillingEnabled(ticket.billing_enabled == 1);
            if (ticket.billing_enabled == 1) {
                loadBillingHours();
            } else {
                setLoading(false);
            }
        }
    }, [ticket]);

    const loadBillingHours = async () => {
        try {
            setLoading(true);
            const response = await api.get(`/api/helpdesk/tickets/${ticket.ticket_id}/billing/hours`);
            if (response.data.status === 1) {
                setBillingHours(response.data.billing_hours || []);
            }
        } catch (error) {
            console.error('Error loading billing hours:', error);
            toast.error('Failed to load billing hours');
        } finally {
            setLoading(false);
        }
    };

    const handleEnableBilling = async (e) => {
        e.preventDefault();
        
        setEnablingBilling(true);
        try {
            const response = await api.post(`/api/helpdesk/tickets/${ticket.ticket_id}/billing/enable`, billingConfig);
            
            if (response.data.status === 1) {
                toast.success('Billing enabled successfully');
                setBillingEnabled(true);
                setShowEnableForm(false);
                
                // Reload ticket to get updated data
                if (onTicketUpdate) {
                    onTicketUpdate();
                }
                
                // Load billing hours (will be empty initially)
                loadBillingHours();
            } else {
                toast.error(response.data.message || 'Failed to enable billing');
            }
        } catch (error) {
            console.error('Error enabling billing:', error);
            toast.error(error.response?.data?.message || 'Failed to enable billing');
        } finally {
            setEnablingBilling(false);
        }
    };

    const handleDisableBilling = async () => {
        if (!window.confirm('Are you sure you want to disable billing for this ticket? This will not delete existing billing records.')) {
            return;
        }

        setDisablingBilling(true);
        try {
            const response = await api.post(`/api/helpdesk/tickets/${ticket.ticket_id}/billing/disable`);
            
            if (response.data.status === 1) {
                toast.success('Billing disabled successfully');
                setBillingEnabled(false);
                
                // Reload ticket
                if (onTicketUpdate) {
                    onTicketUpdate();
                }
            } else {
                toast.error(response.data.message || 'Failed to disable billing');
            }
        } catch (error) {
            console.error('Error disabling billing:', error);
            toast.error(error.response?.data?.message || 'Failed to disable billing');
        } finally {
            setDisablingBilling(false);
        }
    };

    const handleApproveHours = async (hourId) => {
        const notes = prompt('Enter review notes (optional):');
        if (notes === null) return; // User cancelled

        setProcessingAction(hourId);
        try {
            const response = await api.post(
                `/api/helpdesk/tickets/${ticket.ticket_id}/billing/hours/${hourId}/approve`,
                { review_notes: notes }
            );
            
            if (response.data.status === 1) {
                toast.success('Billing hours approved successfully');
                loadBillingHours();
                
                // Reload ticket to update totals
                if (onTicketUpdate) {
                    onTicketUpdate();
                }
            } else {
                toast.error(response.data.message || 'Failed to approve billing hours');
            }
        } catch (error) {
            console.error('Error approving billing hours:', error);
            toast.error(error.response?.data?.message || 'Failed to approve billing hours');
        } finally {
            setProcessingAction(null);
        }
    };

    const handleRejectHours = async (hourId) => {
        const notes = prompt('Enter reason for rejection (required):');
        if (!notes || notes.trim() === '') {
            toast.error('Rejection reason is required');
            return;
        }

        setProcessingAction(hourId);
        try {
            const response = await api.post(
                `/api/helpdesk/tickets/${ticket.ticket_id}/billing/hours/${hourId}/reject`,
                { review_notes: notes }
            );
            
            if (response.data.status === 1) {
                toast.success('Billing hours rejected successfully');
                loadBillingHours();
                
                // Reload ticket to update totals
                if (onTicketUpdate) {
                    onTicketUpdate();
                }
            } else {
                toast.error(response.data.message || 'Failed to reject billing hours');
            }
        } catch (error) {
            console.error('Error rejecting billing hours:', error);
            toast.error(error.response?.data?.message || 'Failed to reject billing hours');
        } finally {
            setProcessingAction(null);
        }
    };

    const getStatusIcon = (status) => {
        switch(status?.toLowerCase()) {
            case 'approved':
                return <CheckCircle className="w-4 h-4 text-green-600" />;
            case 'rejected':
                return <XCircle className="w-4 h-4 text-red-600" />;
            case 'pending':
            default:
                return <AlertCircle className="w-4 h-4 text-yellow-600" />;
        }
    };

    const getStatusBadgeClass = (status) => {
        switch(status?.toLowerCase()) {
            case 'approved':
                return 'bg-green-100 text-green-800';
            case 'rejected':
                return 'bg-red-100 text-red-800';
            case 'pending':
            default:
                return 'bg-yellow-100 text-yellow-800';
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-ZA', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch (e) {
            return dateString;
        }
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            return date.toLocaleString('en-ZA', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (e) {
            return dateString;
        }
    };

    const totalHours = billingHours.reduce((sum, entry) => sum + parseFloat(entry.hours_worked || 0), 0);
    const pendingHours = billingHours.filter(h => h.status === 'pending').reduce((sum, entry) => sum + parseFloat(entry.hours_worked || 0), 0);
    const approvedHours = billingHours.filter(h => h.status === 'approved').reduce((sum, entry) => sum + parseFloat(entry.hours_worked || 0), 0);
    const hourlyRate = parseFloat(ticket?.hourly_rate || 0);

    // Check if ticket has a supplier
    if (!ticket?.supplier_id) {
        return (
            <div className="text-center py-12">
                <DollarSign className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Supplier Assigned</h3>
                <p className="text-gray-600">
                    This ticket must be escalated to a supplier before billing can be enabled.
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Billing Status Header */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex justify-between items-start">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                            <DollarSign className="w-5 h-5 mr-2 text-blue-600" />
                            Supplier Billing Management
                        </h3>
                        <p className="text-sm text-gray-600 mt-1">
                            Manage billable hours and rates for supplier work
                        </p>
                    </div>
                    <div>
                        {billingEnabled ? (
                            <button
                                onClick={handleDisableBilling}
                                disabled={disablingBilling}
                                className="px-4 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 transition-colors duration-200 text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                            >
                                {disablingBilling && (
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-red-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                )}
                                {disablingBilling ? 'Disabling...' : 'Disable Billing'}
                            </button>
                        ) : (
                            <button
                                onClick={() => setShowEnableForm(true)}
                                disabled={enablingBilling}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Enable Billing
                            </button>
                        )}
                    </div>
                </div>

                {/* Enable Billing Form */}
                {showEnableForm && !billingEnabled && (
                    <form onSubmit={handleEnableBilling} className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <h4 className="text-md font-semibold text-gray-900 mb-4">Configure Billing</h4>
                        
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Hourly Rate (Optional)
                                </label>
                                <div className="relative">
                                    <span className="absolute left-3 top-2 text-gray-500">R</span>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={billingConfig.hourly_rate}
                                        onChange={(e) => setBillingConfig({...billingConfig, hourly_rate: e.target.value})}
                                        className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="0.00"
                                    />
                                </div>
                                <p className="text-xs text-gray-500 mt-1">
                                    Set the hourly rate for supplier work (optional, for cost estimation)
                                </p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Billing Notes (Optional)
                                </label>
                                <textarea
                                    value={billingConfig.billing_notes}
                                    onChange={(e) => setBillingConfig({...billingConfig, billing_notes: e.target.value})}
                                    rows="3"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Add any instructions or notes for the supplier regarding billing..."
                                />
                            </div>
                        </div>

                        <div className="flex justify-end space-x-3 mt-4">
                            <button
                                type="button"
                                onClick={() => setShowEnableForm(false)}
                                disabled={enablingBilling}
                                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={enablingBilling}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                            >
                                {enablingBilling ? (
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                ) : (
                                    <Save className="w-4 h-4 inline mr-2" />
                                )}
                                {enablingBilling ? 'Enabling...' : 'Enable Billing'}
                            </button>
                        </div>
                    </form>
                )}
            </div>

            {/* Billing Statistics */}
            {billingEnabled && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                        <p className="text-sm font-medium text-blue-900">Total Hours</p>
                        <p className="text-2xl font-bold text-blue-700 mt-1">{totalHours.toFixed(2)}</p>
                    </div>

                    <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                        <p className="text-sm font-medium text-yellow-900">Pending Review</p>
                        <p className="text-2xl font-bold text-yellow-700 mt-1">{pendingHours.toFixed(2)}</p>
                    </div>

                    <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                        <p className="text-sm font-medium text-green-900">Approved Hours</p>
                        <p className="text-2xl font-bold text-green-700 mt-1">{approvedHours.toFixed(2)}</p>
                    </div>

                    {hourlyRate > 0 && (
                        <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                            <p className="text-sm font-medium text-purple-900">Est. Total Cost</p>
                            <p className="text-2xl font-bold text-purple-700 mt-1">R{(approvedHours * hourlyRate).toFixed(2)}</p>
                        </div>
                    )}
                </div>
            )}

            {/* Billing Hours List */}
            {billingEnabled && (
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <h4 className="text-md font-semibold text-gray-900 mb-4">Submitted Hours</h4>
                    
                    {loading ? (
                        <div className="flex justify-center items-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                            <span className="ml-2 text-gray-600">Loading billing hours...</span>
                        </div>
                    ) : billingHours.length === 0 ? (
                        <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
                            <Clock className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                            <p>No billing hours submitted yet</p>
                            <p className="text-sm">The supplier can submit hours through their portal</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {billingHours.map((entry) => (
                                <div key={entry.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                    <div className="flex justify-between items-start">
                                        <div className="flex-1">
                                            <div className="flex items-center space-x-3 mb-2">
                                                <span className="font-semibold text-lg text-gray-900">
                                                    {parseFloat(entry.hours_worked).toFixed(2)} hours
                                                </span>
                                                <span className="text-gray-500">•</span>
                                                <span className="text-sm text-gray-600">
                                                    {formatDate(entry.work_date)}
                                                </span>
                                                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(entry.status)}`}>
                                                    {getStatusIcon(entry.status)}
                                                    <span className="ml-1 capitalize">{entry.status || 'pending'}</span>
                                                </span>
                                            </div>

                                            <p className="text-sm text-gray-700 mb-2">
                                                {entry.work_description}
                                            </p>

                                            <div className="text-xs text-gray-500 space-y-1">
                                                <p>
                                                    Submitted by: {entry.supplier_user_name || 'Unknown'} ({entry.supplier_user_email || 'N/A'})
                                                </p>
                                                <p>
                                                    Submitted: {formatDateTime(entry.submitted_at)}
                                                </p>
                                                {entry.reviewed_at && (
                                                    <p>
                                                        Reviewed: {formatDateTime(entry.reviewed_at)} by {entry.reviewed_by_name || 'Unknown'}
                                                    </p>
                                                )}
                                            </div>

                                            {entry.review_notes && (
                                                <div className="mt-3 pt-3 border-t border-gray-200">
                                                    <p className="text-xs font-medium text-gray-700 mb-1">Review Notes:</p>
                                                    <p className="text-xs text-gray-600 bg-white rounded p-2">
                                                        {entry.review_notes}
                                                    </p>
                                                </div>
                                            )}
                                        </div>

                                        {entry.status === 'pending' && (
                                            <div className="ml-4 flex space-x-2">
                                                <button
                                                    onClick={() => handleApproveHours(entry.id)}
                                                    disabled={processingAction === entry.id}
                                                    className="p-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed relative"
                                                    title="Approve"
                                                >
                                                    {processingAction === entry.id ? (
                                                        <svg className="animate-spin h-5 w-5 text-green-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                        </svg>
                                                    ) : (
                                                        <CheckCircle className="w-5 h-5" />
                                                    )}
                                                </button>
                                                <button
                                                    onClick={() => handleRejectHours(entry.id)}
                                                    disabled={processingAction === entry.id}
                                                    className="p-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed relative"
                                                    title="Reject"
                                                >
                                                    {processingAction === entry.id ? (
                                                        <svg className="animate-spin h-5 w-5 text-red-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                        </svg>
                                                    ) : (
                                                        <XCircle className="w-5 h-5" />
                                                    )}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default BillingManagementTab;
